import { useEffect, useState, type FormEvent } from 'react';
import { FormField } from '../shared/form';
import { CatalogDialog, PRIMARY_BUTTON, SECONDARY_BUTTON } from '../modelcatalog/CatalogDialog';
import { catalogFormErrors, type CatalogFormErrors } from '../modelcatalog/catalogErrors';
import { PromptCachingSection } from '../modelconfigs/PromptCachingSection';
import { modelLabel, validateAgainstModel, type ModelConfigFormData } from '../modelconfigs/modelConfigRules';
import { useCreateModelConfig, useUpdateModelConfig } from '../../hooks/useModelConfigs';
import type { CreateModelConfigInput, Model, ModelConfig, UpdateModelConfigInput } from '../../types';

/**
 * Add or edit a Model Config from its Model (task 1167, mockup Frame 3).
 *
 * Opened from a Model row's menu, the "Add config on this Model" row under an
 * expanded Model, or a config row's Edit. The Model is fixed — named in the
 * title and the description — so the dialog asks only for the config's own
 * choices, one column, in the same `CatalogDialog` frame Add provider and Add
 * model use. The caps are the retired config form's rules
 * (`modelConfigRules`), and the Prompt caching section is that form's section,
 * following the Model's capability.
 *
 * On success the dialog closes and the Model's inline configs refetch; nothing
 * navigates.
 */

interface ModelConfigDialogProps {
  open: boolean;
  onClose: () => void;
  /** The Model the config runs on. */
  model: Model | null;
  /** Edit this config; absent = create one on `model`. */
  config?: ModelConfig | null;
}

const EMPTY_ERRORS: CatalogFormErrors = { fields: {}, general: null };

const initialValues = (model: Model | null, config?: ModelConfig | null): ModelConfigFormData => ({
  name: config?.name ?? '',
  modelId: model?.id ?? '',
  maxOutputTokens: config?.maxOutputTokens ?? Math.min(4096, model?.maxOutputTokens ?? 4096),
  temperature: config?.temperature ?? 0.7,
  contextBudgetTokens: config?.contextBudgetTokens ?? '',
  promptCaching: config?.promptCaching ?? true,
});

const numberOrBlank = (raw: string): number | '' => (raw === '' ? '' : Number(raw));

export function ModelConfigDialog({ open, onClose, model, config }: ModelConfigDialogProps) {
  const create = useCreateModelConfig();
  const update = useUpdateModelConfig();
  const editing = !!config;
  const [values, setValues] = useState<ModelConfigFormData>(() => initialValues(model, config));
  const [errors, setErrors] = useState<CatalogFormErrors>(EMPTY_ERRORS);
  const [attempted, setAttempted] = useState(false);
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    setValues(initialValues(model, config));
    setErrors(EMPTY_ERRORS);
    setAttempted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, model?.id, config?.id]);

  if (!model) return null;

  const label = modelLabel(model);
  // The live caps check, so a number typed earlier shows its error as soon as
  // it is out of range — not only after Save.
  const ruleErrors = validateAgainstModel(values, model);
  const fieldError = (field: keyof ModelConfigFormData, serviceField = field as string) =>
    errors.fields[serviceField] ?? (attempted || values[field] !== '' ? ruleErrors[field] : undefined);
  const temperatureNotSent = model.supportsTemperature === false;

  const set = <K extends keyof ModelConfigFormData>(field: K, value: ModelConfigFormData[K]) =>
    setValues((previous) => ({ ...previous, [field]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(ruleErrors).length > 0) return;

    const name = values.name.trim();
    const base = {
      maxOutputTokens: Number(values.maxOutputTokens),
      temperature: Number(values.temperature),
      promptCaching: values.promptCaching,
    };

    setErrors(EMPTY_ERRORS);
    try {
      if (config) {
        const data: UpdateModelConfigInput = {
          ...base,
          ...(name ? { name } : {}),
          // Emptying the budget clears it: the config uses the whole window again.
          contextBudgetTokens: values.contextBudgetTokens === '' ? null : Number(values.contextBudgetTokens),
        };
        await update.mutateAsync({ id: config.id, data });
      } else {
        const data: CreateModelConfigInput = {
          ...base,
          modelId: model.id,
          // The name defaults to the Model's id, the handle seeds and lookups use.
          name: name || model.modelId,
          ...(values.contextBudgetTokens !== '' ? { contextBudgetTokens: Number(values.contextBudgetTokens) } : {}),
        };
        await create.mutateAsync(data);
      }
      onClose();
    } catch (error) {
      setErrors(catalogFormErrors(error, 'name'));
    }
  };

  return (
    <CatalogDialog
      open={open}
      onClose={onClose}
      busy={busy}
      title={editing ? config?.name ?? `Config on ${label}` : `Add config — ${label}`}
      description={
        <span data-testid="model-config-dialog-model">
          {model.provider?.displayName ?? model.provider?.key ?? 'Model'} ·{' '}
          <span className="font-mono">{model.modelId}</span> · {model.contextWindow.toLocaleString('en-US')} context ·{' '}
          {model.maxOutputTokens.toLocaleString('en-US')} max output
        </span>
      }
      testId="model-config-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <FormField
            label="Name"
            name="model-config-name"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            error={errors.fields.name}
            placeholder={editing ? undefined : model.modelId}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            How agents and seeds refer to it. Unique among configs.
            {editing ? '' : ` Blank uses ${model.modelId}.`}
          </p>
        </div>

        <div>
          <FormField
            label="Temperature"
            name="model-config-temperature"
            type="number"
            value={values.temperature}
            onChange={(e) => set('temperature', numberOrBlank(e.target.value))}
            error={fieldError('temperature')}
            min={0}
            max={2}
            step={0.1}
          />
          <p data-testid="model-config-temperature-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {temperatureNotSent
              ? `0 to 2. ${label} rejects temperature, so it is kept on the config and not sent to this model.`
              : '0 to 2. Lower is more focused, higher more creative.'}
          </p>
        </div>

        <div>
          <FormField
            label="Output limit"
            name="model-config-max-output"
            type="number"
            value={values.maxOutputTokens}
            onChange={(e) => set('maxOutputTokens', numberOrBlank(e.target.value))}
            error={fieldError('maxOutputTokens')}
            min={1}
            max={model.maxOutputTokens}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            At most {model.maxOutputTokens.toLocaleString('en-US')}, the Model&apos;s maximum.
          </p>
        </div>

        <div>
          <FormField
            label="Context budget"
            name="model-config-context-budget"
            type="number"
            value={values.contextBudgetTokens}
            onChange={(e) => set('contextBudgetTokens', numberOrBlank(e.target.value))}
            error={fieldError('contextBudgetTokens')}
            min={1}
            max={model.contextWindow}
            placeholder={model.contextWindow.toLocaleString('en-US')}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Blank means the whole window, {model.contextWindow.toLocaleString('en-US')} tokens.
          </p>
        </div>

        <PromptCachingSection
          model={model}
          enabled={values.promptCaching}
          onChange={(enabled) => set('promptCaching', enabled)}
        />

        {errors.general && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errors.general}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" disabled={busy} className={PRIMARY_BUTTON}>
            {busy ? 'Saving…' : 'Save config'}
          </button>
        </div>
      </form>
    </CatalogDialog>
  );
}

export default ModelConfigDialog;
